/**
 * WorkspaceChatIntake — conversational shipment creation within the workspace.
 *
 * Combines TWO intake paths in one view:
 *   1. Ocean Quick Fill card — structured form with SmartAddressInput for origin/destination
 *   2. Chat — natural language conversation with Marq (Claude AI)
 *
 * Features:
 *   - SmartAddressInput (port/airport/address autocomplete) on origin & destination
 *   - JSON blocks stripped from old/malformed AI replies
 *   - Simulated streaming: AI replies reveal word-by-word
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useUser } from "@clerk/clerk-react";
import SmartAddressInput from "../quote/SmartAddressInput";
import AgenticMark from "../common/AgenticMark/AgenticMark";
import {
	stripControlJson,
	EMPTY_REPLY_FALLBACK,
} from "./preview/messageContent";
import { friendlyErrorMessage } from "../../../utils/friendlyError";
import { avatarInitials } from "../common/avatarInitials";

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

function cleanAssistantText(text) {
	return stripControlJson(text) || EMPTY_REPLY_FALLBACK;
}

// "2026-05-15" → "Fri, May 15, 2026". Returns "" for empty/invalid input.
// Parses as UTC to avoid the date silently shifting back a day in negative-UTC
// timezones (the typed value "2026-05-15" otherwise becomes May 14 locally).
function formatDateWithWeekday(iso) {
	if (!iso || typeof iso !== "string") return "";
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
	if (!match) return "";
	const [, y, m, d] = match;
	const dt = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
	if (Number.isNaN(dt.getTime())) return "";
	return dt.toLocaleDateString("en-US", {
		weekday: "short",
		month: "short",
		day: "numeric",
		year: "numeric",
		timeZone: "UTC",
	});
}

// ────────────────────────────────────────────────────────────────────────────
// Streaming text hook — reveals text word-by-word
// ────────────────────────────────────────────────────────────────────────────

function useStreamingText(fullText, speed = 18) {
	const [displayed, setDisplayed] = useState("");
	const [done, setDone] = useState(false);

	useEffect(() => {
		if (!fullText) {
			setDisplayed("");
			setDone(true);
			return;
		}
		setDisplayed("");
		setDone(false);
		const words = fullText.split(/(\s+)/);
		let idx = 0;
		const timer = setInterval(() => {
			idx++;
			setDisplayed(words.slice(0, idx).join(""));
			if (idx >= words.length) {
				clearInterval(timer);
				setDone(true);
			}
		}, speed);
		return () => clearInterval(timer);
	}, [fullText, speed]);

	return { displayed, done };
}

/** Wrapper component that streams a single message */
function StreamingBubble({ text }) {
	const { displayed, done } = useStreamingText(text);
	return (
		<div className="ws-bubble">
			{displayed}
			{!done && <span className="ws-typing-cursor" />}
		</div>
	);
}

// ────────────────────────────────────────────────────────────────────────────
// ReadyToSubmitDetails — compact key/value list shown inside the "Ready to
// choose providers" card. Replaces the previous prose summary so the user can
// scan the captured intake at a glance before confirming. Mode-aware: trucking
// shipments surface pickup/delivery/truck-type rows; ocean surfaces port +
// container rows. Falls back to the chat-provided `summary` string if state
// is empty (legacy chat sessions without structured extraction).
// ────────────────────────────────────────────────────────────────────────────
function formatStateValue(v) {
	if (v == null) return null;
	if (typeof v === "boolean") return v ? "Yes" : "No";
	if (typeof v === "number") return String(v);
	if (Array.isArray(v))
		return v.length ? `${v.length} item${v.length === 1 ? "" : "s"}` : null;
	if (typeof v === "object") return null; // structured fields are surfaced as their primitives below
	const s = String(v).trim();
	return s.length === 0 ? null : s;
}

function buildIntakeRows(state) {
	if (!state || typeof state !== "object") return [];
	const isTrucking = state.quoteType === "domestic_trucking";
	// Card shown right before "Confirm details" / RFQ send. Keep it short:
	// origin → destination → type → operational details. Anything also
	// visible in the right rail (Mode, Commodity, Incoterm) is omitted to
	// reduce duplication.
	const pickupCity =
		state.pickupDisplay || state.pickupCity || state.pickupAddress;
	const pickupCombined =
		pickupCity && state.pickupState
			? `${pickupCity}, ${state.pickupState}`
			: pickupCity || state.pickupState;
	const containerCombined =
		state.containerCount && state.containerType
			? `${state.containerCount}× ${state.containerType}`
			: state.containerType || state.containerCount;
	// Backend consolidates pickupDate / deliveryDeadline into cargoReadyDate
	// (chatService.js:381) — same field the right-rail "Ready by" reads, so
	// prefer it here too for consistency. Falls back to the raw extracted
	// fields for legacy sessions persisted before the consolidation.
	const truckingReadyBy =
		formatDateWithWeekday(state.cargoReadyDate) ||
		formatDateWithWeekday(state.pickupDate) ||
		formatDateWithWeekday(state.deliveryDeadline) ||
		state.cargoReadyDate ||
		state.pickupDate ||
		state.deliveryDeadline;
	const oceanReadyBy =
		formatDateWithWeekday(state.cargoReadyDate) || state.cargoReadyDate;
	const candidates = isTrucking
		? [
				["Pickup", pickupCombined],
				["Delivery", state.destinationCity || state.destinationAddress],
				["Truck type", state.truckType],
				["Weight (lbs)", state.totalWeightLbs],
				["Ready by", truckingReadyBy],
			]
		: [
				["Origin", state.originPortDisplayName || state.originPort],
				[
					"Destination",
					state.destinationPortDisplayName || state.destinationPort,
				],
				["Container", containerCombined],
				["Ready by", oceanReadyBy],
				["Total weight (kg)", state.totalWeight],
			];
	return candidates
		.map(([label, raw]) => [label, formatStateValue(raw)])
		.filter(([, v]) => v != null);
}

function ReadyToSubmitDetails({ state, summary }) {
	const rows = buildIntakeRows(state);
	if (rows.length === 0) {
		if (!summary) return null;
		return (
			<div
				style={{
					fontSize: 13,
					color: "var(--text-secondary, #6B6A67)",
					lineHeight: 1.5,
				}}
			>
				{summary}
			</div>
		);
	}
	return (
		<div className="ws-intake-details">
			{rows.map(([label, value]) => (
				<div className="ws-intake-row" key={label}>
					<span className="ws-intake-key">{label}</span>
					<span className="ws-intake-val">{value}</span>
				</div>
			))}
		</div>
	);
}

// ────────────────────────────────────────────────────────────────────────────
// Quick Fill card — mode-aware (ocean freight OR domestic trucking)
//
// Aaron's UAT spec: the form was hard-coded to ocean freight, which silently
// produced ocean-shaped data for domestic lanes (Boise → NYC ended up as a
// 40HC container shipment). Fix: explicit mode selector at the top, no
// container default, conditional fields, mode-aware chat-handoff message.
// Auto-suggest mode from origin/destination country when both sides resolve
// to address/port records — user can still override.
// ────────────────────────────────────────────────────────────────────────────

const CONTAINER_TYPES = ["20GP", "40GP", "40HC", "45HC"];
const TRUCK_TYPES = ["dry_van", "reefer", "flatbed", "step_deck", "hotshot"];
const TRUCK_TYPE_LABELS = {
	dry_van: "Dry van",
	reefer: "Reefer",
	flatbed: "Flatbed",
	step_deck: "Step deck",
	hotshot: "Hotshot",
};
// Truckload mode decides WHICH CARRIERS receive the RFQ (v2_067). "Both"
// belongs to the carrier (it hauls FTL and LTL), never to a request: every
// request says which of the two it needs priced, so the form requires one.
const SHIPMENT_MODES = ["FTL", "LTL"];
const SHIPMENT_MODE_LABELS = {
	FTL: "FTL — full truckload",
	LTL: "LTL — less than truckload",
};
// Incoterms 2020 — same vocabulary as /shipments/new (NewQuotePage). Keeping
// the two intake surfaces aligned avoids a third source of truth.
const INCOTERMS = [
	"EXW",
	"FCA",
	"FOB",
	"CFR",
	"CIF",
	"CIP",
	"CPT",
	"DAP",
	"DDP",
];

// Heuristic: when both sides resolve to US addresses/ports, default to
// domestic_trucking. SmartAddressInput emits { type, countryCode, ... } for
// both port and address selections, so the same check works either way.
const inferQuoteType = (origin, destination) => {
	if (!origin || !destination) return null;
	const oc = origin.countryCode || origin.country;
	const dc = destination.countryCode || destination.country;
	if (!oc || !dc) return null;
	if (oc === "US" && dc === "US") return "domestic_trucking";
	return "ocean_freight";
};

function QuickFillCard({
	onSubmit,
	collapsed,
	onToggle,
	initialState,
	onPatchState,
}) {
	const [quoteType, setQuoteType] = useState(null); // 'ocean_freight' | 'domestic_trucking' | null
	const [modeTouchedByUser, setModeTouchedByUser] = useState(false);
	const [origin, setOrigin] = useState(null);
	const [destination, setDestination] = useState(null);
	// Ocean fields — no container preselected (Aaron: shipper must choose)
	const [containerType, setContainerType] = useState("");
	const [containerCount, setContainerCount] = useState(1);
	const [incoterm, setIncoterm] = useState("FOB");
	// Trucking fields — also no truck-type preselected
	const [truckType, setTruckType] = useState("");
	const [shipmentMode, setShipmentMode] = useState(""); // 'FTL' | 'LTL' | ''
	const [totalWeightLbs, setTotalWeightLbs] = useState("");
	const [pickupDate, setPickupDate] = useState("");
	// Shared
	const [commodity, setCommodity] = useState("");
	const [cargoReadyDate, setCargoReadyDate] = useState("");
	const [formError, setFormError] = useState(null);

	const today = new Date().toISOString().slice(0, 10);

	// Auto-suggest mode from origin/destination country when both resolve. Only
	// overrides when the user hasn't manually picked a mode yet — once they
	// click a tab the heuristic stops fighting them.
	useEffect(() => {
		if (modeTouchedByUser) return;
		const inferred = inferQuoteType(origin, destination);
		if (inferred && inferred !== quoteType) {
			setQuoteType(inferred);
		}
	}, [origin, destination, modeTouchedByUser, quoteType]);

	// One-way sync from chat-extracted state. Only populates fields that the
	// user hasn't explicitly touched (still holds the constructor default), so
	// typing into Origin won't be overwritten by a later state extraction.
	useEffect(() => {
		if (!initialState) return;
		if (initialState.quoteType && !modeTouchedByUser && !quoteType) {
			setQuoteType(initialState.quoteType);
		}
		if (initialState.originPort && !origin) {
			setOrigin({ displayName: initialState.originPort, locode: "" });
		}
		if (initialState.destinationPort && !destination) {
			setDestination({ displayName: initialState.destinationPort, locode: "" });
		}
		if (initialState.containerType && !containerType) {
			setContainerType(initialState.containerType);
		}
		if (initialState.containerCount && containerCount === 1) {
			setContainerCount(initialState.containerCount);
		}
		if (initialState.incoterm && incoterm === "FOB") {
			setIncoterm(initialState.incoterm);
		}
		if (initialState.truckType && !truckType) {
			setTruckType(initialState.truckType);
		}
		if (
			(initialState.shipmentMode === "FTL" ||
				initialState.shipmentMode === "LTL") &&
			!shipmentMode
		) {
			setShipmentMode(initialState.shipmentMode);
		}
		if (initialState.totalWeightLbs && !totalWeightLbs) {
			setTotalWeightLbs(String(initialState.totalWeightLbs));
		}
		if (initialState.commodity && !commodity) {
			setCommodity(initialState.commodity);
		}
		if (initialState.cargoReadyDate && !cargoReadyDate) {
			setCargoReadyDate(String(initialState.cargoReadyDate).slice(0, 10));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialState]);

	const getDisplayName = (val) => {
		if (!val) return "";
		if (typeof val === "string") return val;
		return val.displayName || val.locode || "";
	};

	const pickMode = (mode) => {
		setQuoteType(mode);
		setModeTouchedByUser(true);
		setFormError(null);
	};

	const handleContinue = async () => {
		const originName = getDisplayName(origin);
		const destName = getDisplayName(destination);
		if (!quoteType) return setFormError("Choose ocean or trucking first");
		if (!originName) return setFormError("Origin is required");
		if (!destName) return setFormError("Destination is required");
		if (quoteType === "domestic_trucking" && !shipmentMode)
			return setFormError("Choose FTL or LTL");
		setFormError(null);

		// Pre-seed the chat session's structured state. Same pattern as before
		// (C1 / INCO-01) but per-mode payload — never send container fields on a
		// trucking submit and never send truckType on an ocean submit.
		const patch = {
			quoteType,
			commodity: commodity || undefined,
		};
		if (quoteType === "ocean_freight") {
			patch.incoterm = incoterm;
			// Route SmartAddressInput's payload by type so an address doesn't silently
			// land in originPort and get treated as a handoff port by the BE.
			const applyOceanSide = (side, payload, fallbackName) => {
				const lkey = side === "origin" ? "originLocode" : "destinationLocode";
				const akey =
					side === "origin" ? "originAddressJson" : "destinationAddressJson";
				const pkey = side === "origin" ? "originPort" : "destinationPort";
				const obj = payload && typeof payload === "object" ? payload : null;
				if (obj?.type === "port" && obj.locode) {
					patch[lkey] = obj.locode;
					patch[akey] = null;
					patch[pkey] = obj.displayName || obj.locode;
				} else if (obj?.type === "address") {
					patch[akey] = {
						formattedAddress: obj.displayName,
						displayName: obj.displayName,
						street: obj.street || null,
						city: obj.city || null,
						state: obj.state || null,
						stateCode: obj.stateCode || null,
						zip: obj.zip || null,
						country: obj.country || null,
						countryCode: obj.countryCode || null,
						lat: obj.latitude || null,
						lng: obj.longitude || null,
					};
					patch[lkey] = null;
					patch[pkey] = obj.displayName || fallbackName;
				} else {
					patch[pkey] = fallbackName;
				}
			};
			applyOceanSide("origin", origin, originName);
			applyOceanSide("destination", destination, destName);
			if (containerType) patch.containerType = containerType;
			if (containerCount) patch.containerCount = containerCount;
			if (cargoReadyDate) patch.cargoReadyDate = cargoReadyDate;
		} else {
			// Domestic trucking. Origin/destination flow into the trucking-specific
			// fields the chatService normalizer (v2_033) already understands.
			patch.pickupAddress = originName;
			patch.destinationAddress = destName;
			if (truckType) patch.truckType = truckType;
			patch.shipmentMode = shipmentMode;
			if (totalWeightLbs)
				patch.totalWeightLbs = Number(totalWeightLbs) || undefined;
			if (pickupDate) patch.cargoReadyDate = pickupDate;
		}
		if (onPatchState) {
			try {
				await onPatchState(patch);
			} catch (_err) {
				/* chat path is the safety net */
			}
		}

		// Build a mode-appropriate handoff message so the LLM doesn't get pushed
		// toward the wrong lane. The legs/incoterm/truck-type fields above are
		// already pre-seeded via patchSessionState; this prose just gives the
		// assistant context to greet the user with.
		let msg;
		if (quoteType === "ocean_freight") {
			const parts = [`from ${originName}`, `to ${destName}`];
			if (containerType) {
				parts.push(
					`${containerCount}x ${containerType} container${containerCount > 1 ? "s" : ""}`,
				);
			}
			if (incoterm) parts.push(`incoterm ${incoterm}`);
			if (commodity) parts.push(`carrying ${commodity}`);
			if (cargoReadyDate) parts.push(`cargo ready ${cargoReadyDate}`);
			msg = `I need an ocean freight quote ${parts.join(", ")}.`;
		} else {
			const parts = [];
			if (truckType) {
				parts.push(
					`a ${TRUCK_TYPE_LABELS[truckType] || truckType.replace(/_/g, " ")} pickup`,
				);
			} else {
				parts.push("a truckload pickup");
			}
			parts.push(`at ${originName}`);
			parts.push(`delivering to ${destName}`);
			parts.push(shipmentMode);
			if (totalWeightLbs) parts.push(`${totalWeightLbs} lbs`);
			if (pickupDate) parts.push(`pickup ${pickupDate}`);
			if (commodity) parts.push(`carrying ${commodity}`);
			msg = `I need a domestic trucking quote — ${parts.join(", ")}.`;
		}
		onSubmit(msg);
	};

	// Header copy mirrors the chosen mode so the user always knows which
	// schema they're filling in.
	const headerLabel =
		quoteType === "ocean_freight"
			? "Ocean freight quick fill"
			: quoteType === "domestic_trucking"
				? "Domestic trucking quick fill"
				: "Quick fill";

	if (collapsed) {
		return (
			<button
				onClick={onToggle}
				style={{
					alignSelf: "flex-start",
					maxWidth: "min(520px, 100%)",
					boxSizing: "border-box",
					padding: "10px 16px",
					fontSize: 13,
					fontWeight: 500,
					background: "var(--bg-default, #fff)",
					border: "1px solid var(--border-default, #E9E8E8)",
					borderRadius: 8,
					cursor: "pointer",
					color: "var(--text-secondary, #6B6A67)",
					fontFamily: "inherit",
					textAlign: "left",
					display: "flex",
					alignItems: "center",
					gap: 8,
				}}
			>
				{headerLabel} — click to expand
			</button>
		);
	}

	const fieldStyle = {
		display: "flex",
		flexDirection: "column",
		gap: 4,
		minWidth: 0,
	};
	const labelStyle = {
		fontSize: 12,
		fontWeight: 500,
		color: "var(--text-secondary, #6B6A67)",
	};
	const inputStyle = {
		padding: "8px 10px",
		border: "1px solid var(--border-default, #E9E8E8)",
		borderRadius: 6,
		fontSize: 13,
		fontFamily: "inherit",
		background: "var(--bg-default, #fff)",
		color: "var(--text-primary, #22201C)",
		width: "100%",
		boxSizing: "border-box",
		minWidth: 0,
	};

	// Mode-tab styling inlined to keep this component self-contained.
	const modeTabBase = {
		flex: 1,
		padding: "8px 12px",
		fontSize: 13,
		fontWeight: 500,
		fontFamily: "inherit",
		cursor: "pointer",
		textAlign: "center",
		border: "1px solid var(--border-default, #E9E8E8)",
		background: "var(--bg-default, #fff)",
		color: "var(--text-secondary, #6B6A67)",
		transition: "background 120ms, color 120ms",
	};
	const modeTabActive = {
		...modeTabBase,
		background: "var(--umber-500, #8F4430)",
		borderColor: "var(--umber-500, #8F4430)",
		color: "#FFFFFF",
	};

	return (
		<div
			data-testid="ocean-quick-fill-card"
			data-mode={quoteType || "unset"}
			style={{
				alignSelf: "flex-start",
				width: "100%",
				maxWidth: "min(560px, 100%)",
				boxSizing: "border-box",
				flexShrink: 0,
				background: "var(--bg-default, #fff)",
				border: "1px solid var(--border-default, #E9E8E8)",
				borderTop: "3px solid var(--umber-500, #8F4430)",
				borderRadius: 8,
				overflow: "visible",
			}}
		>
			<div
				style={{
					padding: "10px 16px",
					borderBottom: "1px solid var(--border-default, #E9E8E8)",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					gap: 8,
					flexWrap: "wrap",
				}}
			>
				<span
					style={{
						fontSize: 13,
						fontWeight: 500,
						color: "var(--text-primary, #22201C)",
					}}
				>
					{headerLabel}
				</span>
				<button
					onClick={onToggle}
					style={{
						background: "none",
						border: "none",
						cursor: "pointer",
						fontSize: 12,
						color: "var(--text-secondary, #6B6A67)",
						fontFamily: "inherit",
					}}
				>
					Collapse ▴
				</button>
			</div>

			{/* Mode selector — explicit choice, no ocean default */}
			<div
				data-testid="quick-fill-mode-tabs"
				role="tablist"
				style={{
					display: "flex",
					gap: 0,
					padding: "12px 16px 0",
					flexWrap: "wrap",
				}}
			>
				<button
					type="button"
					role="tab"
					data-testid="mode-ocean"
					aria-selected={quoteType === "ocean_freight"}
					onClick={() => pickMode("ocean_freight")}
					style={{
						...(quoteType === "ocean_freight" ? modeTabActive : modeTabBase),
						borderRadius: "6px 0 0 6px",
						borderRight: "none",
					}}
				>
					Ocean Freight
				</button>
				<button
					type="button"
					role="tab"
					data-testid="mode-trucking"
					aria-selected={quoteType === "domestic_trucking"}
					onClick={() => pickMode("domestic_trucking")}
					style={{
						...(quoteType === "domestic_trucking"
							? modeTabActive
							: modeTabBase),
						borderRadius: "0 6px 6px 0",
					}}
				>
					Domestic Trucking
				</button>
			</div>

			<div
				data-testid="ocean-quick-fill-grid"
				style={{
					padding: 16,
					display: "grid",
					gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
					gap: 12,
				}}
			>
				<div style={fieldStyle}>
					<span style={labelStyle}>
						Origin <span style={{ color: "#8B3A3A" }}>*</span>
					</span>
					<SmartAddressInput
						value={origin}
						onChange={(v) => {
							setOrigin(v);
							setFormError(null);
						}}
						placeholder="City, port, or address"
						side="origin"
					/>
				</div>
				<div style={fieldStyle}>
					<span style={labelStyle}>
						Destination <span style={{ color: "#8B3A3A" }}>*</span>
					</span>
					<SmartAddressInput
						value={destination}
						onChange={(v) => {
							setDestination(v);
							setFormError(null);
						}}
						placeholder="City, port, or address"
						side="destination"
					/>
				</div>

				{/* Ocean-only fields */}
				{quoteType === "ocean_freight" && (
					<>
						<div style={fieldStyle}>
							<span style={labelStyle}>Container</span>
							<div style={{ display: "flex", gap: 6, minWidth: 0 }}>
								<select
									data-testid="container-type-select"
									style={{
										...inputStyle,
										flex: 1,
										color: containerType
											? undefined
											: "var(--text-tertiary, #999897)",
									}}
									value={containerType}
									onChange={(e) => setContainerType(e.target.value)}
								>
									<option value="">Select container…</option>
									{CONTAINER_TYPES.map((t) => (
										<option key={t} value={t}>
											{t}
										</option>
									))}
								</select>
								<input
									type="number"
									min={1}
									max={20}
									style={{
										...inputStyle,
										width: 64,
										flex: "0 0 64px",
										textAlign: "center",
									}}
									value={containerCount}
									onChange={(e) =>
										setContainerCount(Math.max(1, Number(e.target.value) || 1))
									}
									disabled={!containerType}
								/>
							</div>
						</div>
						<div style={fieldStyle}>
							<span style={labelStyle}>Cargo Ready Date</span>
							<input
								type="date"
								min={today}
								style={inputStyle}
								value={cargoReadyDate}
								onChange={(e) => setCargoReadyDate(e.target.value)}
							/>
							{cargoReadyDate && (
								<span
									style={{
										fontSize: 11,
										color: "var(--text-secondary, #6B6A67)",
										marginTop: 2,
									}}
								>
									{formatDateWithWeekday(cargoReadyDate)}
								</span>
							)}
						</div>
						<div style={fieldStyle}>
							<span style={labelStyle}>Incoterm</span>
							<select
								style={inputStyle}
								value={incoterm}
								onChange={(e) => setIncoterm(e.target.value)}
							>
								{INCOTERMS.map((t) => (
									<option key={t} value={t}>
										{t}
									</option>
								))}
							</select>
						</div>
					</>
				)}

				{/* Trucking-only fields */}
				{quoteType === "domestic_trucking" && (
					<>
						<div style={fieldStyle}>
							<span style={labelStyle}>Truck type</span>
							<select
								data-testid="truck-type-select"
								style={{
									...inputStyle,
									color: truckType
										? undefined
										: "var(--text-tertiary, #999897)",
								}}
								value={truckType}
								onChange={(e) => setTruckType(e.target.value)}
							>
								<option value="">Select truck type…</option>
								{TRUCK_TYPES.map((t) => (
									<option key={t} value={t}>
										{TRUCK_TYPE_LABELS[t]}
									</option>
								))}
							</select>
						</div>
						<div style={fieldStyle}>
							<span style={labelStyle}>Total weight (lbs)</span>
							<input
								type="number"
								min={0}
								style={inputStyle}
								placeholder="e.g. 38000"
								value={totalWeightLbs}
								onChange={(e) => setTotalWeightLbs(e.target.value)}
							/>
						</div>
						<div style={fieldStyle}>
							<span style={labelStyle}>Pickup date</span>
							<input
								type="date"
								min={today}
								style={inputStyle}
								value={pickupDate}
								onChange={(e) => setPickupDate(e.target.value)}
							/>
						</div>
						<div style={fieldStyle}>
							<span style={labelStyle}>
								Truckload mode <span style={{ color: "#8B3A3A" }}>*</span>
							</span>
							<select
								data-testid="shipment-mode-select"
								style={{
									...inputStyle,
									color: shipmentMode
										? undefined
										: "var(--text-tertiary, #999897)",
								}}
								value={shipmentMode}
								onChange={(e) => setShipmentMode(e.target.value)}
							>
								<option value="">Select FTL or LTL…</option>
								{SHIPMENT_MODES.map((m) => (
									<option key={m} value={m}>
										{SHIPMENT_MODE_LABELS[m]}
									</option>
								))}
							</select>
						</div>
					</>
				)}

				<div style={{ ...fieldStyle, gridColumn: "1 / -1" }}>
					<span style={labelStyle}>Commodity (optional)</span>
					<input
						style={inputStyle}
						placeholder="e.g. porcelain tiles, electronics, textiles"
						value={commodity}
						onChange={(e) => setCommodity(e.target.value)}
					/>
				</div>
			</div>

			<div
				data-testid="ocean-quick-fill-footer"
				style={{
					padding: "10px 16px",
					borderTop: "1px solid var(--border-default, #E9E8E8)",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					flexWrap: "wrap",
					gap: 8,
					background: "var(--bg-grey-soft, #FAFAFA)",
				}}
			>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: 4,
						flex: "1 1 220px",
						minWidth: 0,
					}}
				>
					<span
						style={{ fontSize: 12, color: "var(--text-secondary, #6B6A67)" }}
					>
						<span style={{ color: "#8B3A3A" }}>*</span> Required — Marq will ask
						about the rest
					</span>
					{formError && (
						<span style={{ fontSize: 12, color: "#8B3A3A" }}>{formError}</span>
					)}
				</div>
				<button
					data-testid="ocean-quick-fill-continue"
					className="ws-btn ws-btn--primary ws-btn--small"
					onClick={handleContinue}
					disabled={!quoteType}
					style={{
						flexShrink: 0,
						whiteSpace: "nowrap",
						opacity: quoteType ? 1 : 0.5,
					}}
				>
					Continue with Marq →
				</button>
			</div>
		</div>
	);
}

// ────────────────────────────────────────────────────────────────────────────
// State chips — show extracted fields above the chat
// ────────────────────────────────────────────────────────────────────────────

const FIELD_LABELS = {
	quoteType: "Type",
	originPort: "Origin",
	destinationPort: "Destination",
	containerType: "Container",
	containerCount: "Containers",
	commodity: "Commodity",
	totalWeight: "Weight",
	cargoReadyDate: "Cargo ready",
	requestedDeliveryDate: "Delivery by",
	incoterm: "Incoterm",
	truckType: "Truck type",
	shipmentMode: "Mode",
	pickupAddress: "Pickup",
	destinationAddress: "Delivery address",
};

function StateChips({ state }) {
	if (!state || Object.keys(state).length === 0) return null;
	const entries = Object.entries(state).filter(
		([k, v]) => v != null && v !== "" && FIELD_LABELS[k],
	);
	if (entries.length === 0) return null;

	return (
		<div
			style={{
				display: "flex",
				flexWrap: "wrap",
				gap: 6,
				padding: "8px 24px",
				borderBottom: "1px solid var(--border-default, #E9E8E8)",
				flexShrink: 0,
			}}
		>
			{entries.map(([k, v]) => {
				const isDate = k === "cargoReadyDate" || k === "requestedDeliveryDate";
				const display = isDate
					? formatDateWithWeekday(String(v)) || String(v)
					: String(v);
				return (
					<span
						key={k}
						style={{
							fontSize: 12,
							padding: "3px 8px",
							background: "var(--bg-grey-soft, #FAFAFA)",
							border: "1px solid var(--border-default, #E9E8E8)",
							borderRadius: 4,
							color: "var(--text-primary, #22201C)",
						}}
					>
						<span style={{ color: "var(--text-secondary, #6B6A67)" }}>
							{FIELD_LABELS[k]}:{" "}
						</span>
						{display}
					</span>
				);
			})}
		</div>
	);
}

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────

const THOUGHTS = [
	"Drafting reply…",
	"Pulling lane history…",
	"Comparing all-in cost…",
	"Reviewing past patterns…",
	"Cross-checking provider response times…",
	"Normalizing quote terms…",
];

export default function WorkspaceChatIntake({
	sessionId,
	api,
	onShipmentCreated,
	onCancel,
	onStateChange,
}) {
	const { user } = useUser();
	const userInitials = avatarInitials(user);
	const [messages, setMessages] = useState([]);
	const [state, setState] = useState({});
	const [input, setInput] = useState("");
	const [sending, setSending] = useState(false);
	const [readyToSubmit, setReadyToSubmit] = useState(false);
	const [summary, setSummary] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState(null);
	const [lastFailedContent, setLastFailedContent] = useState(null);
	const [loading, setLoading] = useState(true);
	const [formCollapsed, setFormCollapsed] = useState(true);
	const [streamingMsgId, setStreamingMsgId] = useState(null);
	const [currentThought, setCurrentThought] = useState(null);
	const logRef = useRef(null);
	// Synchronous reentrancy guard — `sending` (React state) doesn't update in
	// the same tick, so two synchronous `sendMessage` calls would both pass the
	// gate and fire duplicate POSTs (P1-9).
	const sendingRef = useRef(false);
	const abortRef = useRef(null);
	// Mirror of messages used inside async callbacks so they can read the
	// latest set without re-binding the effect.
	const messagesRef = useRef([]);

	// Cycle thoughts while sending
	useEffect(() => {
		if (!sending) {
			setCurrentThought(null);
			return;
		}
		let idx = 0;
		setCurrentThought(THOUGHTS[0]);
		const timer = setInterval(() => {
			idx = (idx + 1) % THOUGHTS.length;
			setCurrentThought(THOUGHTS[idx]);
		}, 2000);
		return () => clearInterval(timer);
	}, [sending]);

	// Auto-scroll to bottom when messages change
	useEffect(() => {
		if (logRef.current) {
			logRef.current.scrollTop = logRef.current.scrollHeight;
		}
		messagesRef.current = messages;
	}, [messages, sending]);

	// Load session on mount
	useEffect(() => {
		if (!sessionId) return;
		let cancelled = false;
		setLoading(true);
		api
			.getChatSession(sessionId)
			.then((res) => {
				if (cancelled) return;
				const s = res?.data;
				setMessages(s?.messages || []);
				setState(s?.state || {});
				setReadyToSubmit(!!s?.ready_to_submit);
			})
			.catch(
				(err) =>
					!cancelled && setError(err || new Error("Failed to load session")),
			)
			.finally(() => !cancelled && setLoading(false));
		return () => {
			cancelled = true;
		};
	}, [sessionId, api]);

	// Lift extracted state to the parent so the right-rail can mirror what the
	// chat extracted (P0-5).
	useEffect(() => {
		onStateChange?.(state);
	}, [state, onStateChange]);

	// Refresh the session when the tab regains focus, so messages sent in
	// another tab show up here without a manual reload (P1-8).
	const [reconciledToast, setReconciledToast] = useState(false);
	useEffect(() => {
		if (!sessionId) return;
		const onVisibility = async () => {
			if (
				typeof document === "undefined" ||
				document.visibilityState !== "visible"
			)
				return;
			// Skip refetch while a send is in flight — the optimistic message would
			// be wiped before the POST response lands.
			if (sendingRef.current) return;
			try {
				const res = await api.getChatSession(sessionId);
				const s = res?.data;
				if (!s) return;
				const fresh = s.messages || [];
				// Server is the source of truth on refetch. Replacing the list
				// drops any stale optimistic entries whose temp ids (pending-*,
				// asst-*) would otherwise never reconcile with the server UUIDs.
				const sameLength = fresh.length === messagesRef.current.length;
				const sameIds =
					sameLength &&
					fresh.every((m, i) => m.id === messagesRef.current[i]?.id);
				if (!sameIds) {
					setMessages(fresh);
					setReconciledToast(true);
					setTimeout(() => setReconciledToast(false), 3000);
				}
				setState(s.state || {});
				setReadyToSubmit(!!s.ready_to_submit);
			} catch {
				// Best-effort refresh; silently swallow so a transient failure
				// doesn't open an error banner on simple tab switch.
			}
		};
		document.addEventListener("visibilitychange", onVisibility);
		return () => document.removeEventListener("visibilitychange", onVisibility);
	}, [sessionId, api]);

	// Send a message (from chat input or from form card)
	const sendMessage = useCallback(
		async (content) => {
			if (!content || !sessionId) return;
			if (sendingRef.current) return;
			sendingRef.current = true;

			const controller = new AbortController();
			abortRef.current = controller;

			// MTEU-464: any user-initiated send (chat input OR form submit) collapses
			// the Quick Fill card. Sending a chat message means the user has opted
			// into the conversational path; keeping the form expanded above the log
			// forces them to scroll past it. The collapsed pill at the top still
			// re-expands the form on click if they change their mind.
			setFormCollapsed(true);

			setMessages((prev) => [
				...prev,
				{
					id: `pending-${Date.now()}`,
					role: "user",
					content,
					created_at: new Date().toISOString(),
				},
			]);
			setInput("");
			setSending(true);
			setError(null);
			setLastFailedContent(null);

			try {
				const res = await api.postChatMessage(sessionId, content, {
					signal: controller.signal,
				});
				const data = res?.data || {};

				const replyId = `asst-${Date.now()}`;
				setMessages((prev) => [
					...prev,
					{
						id: replyId,
						role: "assistant",
						content: data.reply || "",
						created_at: new Date().toISOString(),
					},
				]);
				setStreamingMsgId(replyId);
				setState(data.state || {});
				setReadyToSubmit(!!data.readyToSubmit);
				setSummary(data.summary || null);

				if (data.submitted && data.submitResult?.shipmentId) {
					onShipmentCreated(data.submitResult.shipmentId);
				}
			} catch (err) {
				if (err?.name === "AbortError") {
					// User cancelled — silent UX, no banner. Restore the typed text so
					// the user doesn't lose what they wrote.
					setInput(content);
				} else {
					setError(err);
					// Restore typed text + remember it for the Retry button.
					setInput(content);
					setLastFailedContent(content);
				}
			} finally {
				sendingRef.current = false;
				abortRef.current = null;
				setSending(false);
			}
		},
		[sessionId, api, onShipmentCreated],
	);

	const cancelSend = useCallback(() => {
		if (abortRef.current) {
			abortRef.current.abort();
		}
	}, []);

	const handleSend = useCallback(() => {
		sendMessage(input.trim());
	}, [input, sendMessage]);

	const handleFormSubmit = useCallback(
		(formMessage) => {
			// sendMessage already collapses the card (MTEU-464). Keeping this thin
			// wrapper for naming clarity at the call site.
			sendMessage(formMessage);
		},
		[sendMessage],
	);

	// C1 / INCO-01 — pre-seed structured state from the Quick Fill form so
	// the LLM doesn't have to re-extract incoterm / container / dates from
	// the prose chat-handoff message. Best-effort: a failure here is logged
	// but does not block the message itself.
	const handlePatchState = useCallback(
		async (partial) => {
			if (!sessionId) return;
			return api.patchChatSessionState(sessionId, partial);
		},
		[sessionId, api],
	);

	const submittingRef = useRef(false);
	const submitAbortRef = useRef(null);
	const handleConfirm = useCallback(async () => {
		if (!sessionId) return;
		if (submittingRef.current) return;
		submittingRef.current = true;
		const controller = new AbortController();
		submitAbortRef.current = controller;
		setSubmitting(true);
		setError(null);
		let succeeded = false;
		try {
			const res = await api.submitChatSession(sessionId, {
				signal: controller.signal,
			});
			const { shipmentId } = res?.data || {};
			if (shipmentId) {
				succeeded = true;
				onShipmentCreated(shipmentId);
			} else {
				setError("Submitted, but no shipment ID returned.");
			}
		} catch (err) {
			if (err?.name === "AbortError") {
				// User cancelled — silent UX, no banner.
				return;
			}
			setError(err || new Error("Failed to submit"));
		} finally {
			submitAbortRef.current = null;
			// On success, the parent will hydrate the new shipment and only
			// then unmount this component (handleShipmentCreated defers the
			// chat→workspace swap). Keeping submitting=true through the wait
			// avoids the button flickering back to "Confirm details" mid-flight.
			if (!succeeded) {
				submittingRef.current = false;
				setSubmitting(false);
			}
		}
	}, [sessionId, api, onShipmentCreated]);

	const cancelSubmit = useCallback(() => {
		if (submitAbortRef.current) {
			submitAbortRef.current.abort();
		}
	}, []);

	const handleKeyDown = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	if (loading) {
		return (
			<div style={{ padding: 40, opacity: 0.6, fontSize: 14 }}>
				Starting conversation...
			</div>
		);
	}

	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				minHeight: 0,
				maxHeight: "100%",
			}}
		>
			{/* ── Header (fixed) ── */}
			<div
				style={{
					padding: "12px 24px",
					borderBottom: "1px solid var(--border-default, #E9E8E8)",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					background: "var(--bg-default, #fff)",
					flexShrink: 0,
				}}
			>
				<div>
					<div
						style={{
							fontSize: 16,
							fontWeight: 500,
							color: "var(--text-primary, #22201C)",
						}}
					>
						New trucking request
					</div>
					<div
						style={{
							fontSize: 13,
							color: "var(--text-secondary, #6B6A67)",
							marginTop: 2,
						}}
					>
						Describe a domestic trucking load, or expand ocean quick fill when
						needed
					</div>
				</div>
				<button
					onClick={onCancel}
					className="ws-btn ws-btn--outline ws-btn--small"
				>
					Cancel
				</button>
			</div>

			{/* ── State chips (fixed) ── */}
			<StateChips state={state} />

			{/* ── Messages area (scrollable, takes remaining space) ── */}
			<div
				ref={logRef}
				className="ws-log"
				style={{ flex: 1, minHeight: 0, overflowY: "auto" }}
			>
				{/* Quick Fill card — first item, before messages */}
				<QuickFillCard
					onSubmit={handleFormSubmit}
					collapsed={formCollapsed}
					onToggle={() => setFormCollapsed((c) => !c)}
					initialState={state}
					onPatchState={handlePatchState}
				/>

				{/* Chat messages */}
				{messages.map((msg, i) => {
					if (msg.role === "system") {
						return (
							<div key={msg.id || i} className="ws-system">
								{stripControlJson(msg.content)}
							</div>
						);
					}
					const isUser = msg.role === "user";
					const cleaned = isUser
						? stripControlJson(msg.content)
						: cleanAssistantText(msg.content);
					const shouldStream = !isUser && msg.id === streamingMsgId;
					return (
						<div
							key={msg.id || i}
							className={`ws-msg ${isUser ? "ws-msg--customer" : "ws-msg--ai"}`}
						>
							{isUser && (
								<div className="ws-avatar ws-avatar--customer">
									{userInitials}
								</div>
							)}
							<div>
								{!isUser && (
									<div className="ws-msg-author ws-msg-author--ai">Marq</div>
								)}
								{shouldStream ? (
									<StreamingBubble text={cleaned} />
								) : (
									<div className="ws-bubble">{cleaned}</div>
								)}
							</div>
						</div>
					);
				})}

				{/* Marq presence — resting when idle, working when sending. Animated
            blob; per-message avatars above are the static M for performance. */}
				<div className="ws-presence-inline">
					<AgenticMark
						state={sending ? "working" : "rest"}
						size={32}
						ink="#22201C"
					/>
					{currentThought && (
						<div className="ws-shimmer-thought">{currentThought}</div>
					)}
					{sending && (
						<button
							type="button"
							onClick={cancelSend}
							aria-label="Cancel send"
							className="ws-btn ws-btn--outline ws-btn--small"
							style={{ marginLeft: 8 }}
						>
							Cancel send
						</button>
					)}
				</div>

				{/* Ready to submit panel — stays mounted while submitting so the
				    user keeps seeing the same card; only the button label changes.
				    Previously we swapped the whole card for a small inline
				    "Creating shipment..." line, which felt like a hard flash. */}
				{readyToSubmit && (
					<div
						style={{
							alignSelf: "flex-start",
							maxWidth: 680,
							width: "100%",
							background: "var(--bg-default, #fff)",
							border: "1px solid var(--border-default, #E9E8E8)",
							borderTop: "3px solid var(--navy-500, #2B2560)",
							borderRadius: 8,
							padding: 16,
							marginTop: 8,
							transition: "opacity 180ms",
							opacity: submitting ? 0.85 : 1,
						}}
					>
						<div
							style={{
								fontSize: 14,
								fontWeight: 500,
								marginBottom: 8,
								color: "var(--text-primary, #22201C)",
							}}
						>
							Ready to choose providers
						</div>
						<ReadyToSubmitDetails state={state} summary={summary} />
						<div
							style={{
								display: "flex",
								gap: 8,
								marginTop: 20,
								alignItems: "center",
							}}
						>
							<button
								className="ws-btn ws-btn--primary"
								onClick={handleConfirm}
								disabled={submitting}
								style={{
									display: "inline-flex",
									alignItems: "center",
									gap: 8,
								}}
							>
								{submitting && (
									<span className="ws-btn-spinner" aria-hidden="true" />
								)}
								{submitting ? "Submitting…" : "Confirm details"}
							</button>
							{submitting && (
								<button
									type="button"
									onClick={cancelSubmit}
									className="ws-btn ws-btn--outline ws-btn--small"
								>
									Cancel
								</button>
							)}
						</div>
					</div>
				)}
			</div>

			{/* ── Reconciled toast (fixed top-right, auto-dismiss) ── */}
			{reconciledToast && (
				<div
					className="ws-toast ws-toast--info"
					role="status"
					aria-live="polite"
				>
					<span className="ws-toast-text">
						Session updated elsewhere — refreshed.
					</span>
				</div>
			)}

			{/* ── Error toast (fixed top-right, no auto-dismiss) ── */}
			{error && (
				<div
					className="ws-toast ws-toast--error"
					role="alert"
					aria-live="polite"
				>
					<span className="ws-toast-text">{friendlyErrorMessage(error)}</span>
					{lastFailedContent && (
						<button
							className="ws-toast-action"
							onClick={() => {
								const content = lastFailedContent;
								setError(null);
								setLastFailedContent(null);
								sendMessage(content);
							}}
						>
							Retry
						</button>
					)}
					<button
						className="ws-toast-dismiss"
						onClick={() => {
							setError(null);
							setLastFailedContent(null);
						}}
						aria-label="Dismiss error"
					>
						×
					</button>
				</div>
			)}

			{/* ── Input bar (anchored to bottom) ── */}
			<div className="ws-input-bar" style={{ flexShrink: 0 }}>
				<input
					className="ws-input"
					placeholder="Type a trucking load... e.g. dry van Cincinnati OH to Phoenix AZ pickup Monday"
					value={input}
					onChange={(e) => setInput(e.target.value)}
					onKeyDown={handleKeyDown}
					disabled={sending || submitting}
				/>
				<button
					className="ws-send-btn"
					onClick={handleSend}
					disabled={!input.trim() || sending || submitting}
					title="Send"
				>
					<svg
						width="18"
						height="18"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<line x1="22" y1="2" x2="11" y2="13" />
						<polygon points="22 2 15 22 11 13 2 9 22 2" />
					</svg>
				</button>
			</div>
		</div>
	);
}
